import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, AlertTriangle, Eye } from "lucide-react";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { getRollouts } from "../../lib/api";
import { formatDate, rollbackRatePercent } from "../../lib/utils";

const STATE_VARIANT = {
  CANARY: "info",
  PHASE_25: "info",
  PHASE_100: "info",
  PAUSED: "warning",
  COMPLETE: "success",
  ROLLED_BACK: "destructive",
};

const STATE_LABEL = {
  CANARY: "Canary (5%)",
  PHASE_25: "Phase 25%",
  PHASE_100: "Phase 100%",
  PAUSED: "Paused",
  COMPLETE: "Complete",
  ROLLED_BACK: "Rolled Back",
};

export default function RolloutsPage() {
  const navigate = useNavigate();
  const [rollouts, setRollouts] = useState([]);
  const [filter, setFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("ALL");

  useEffect(() => {
    getRollouts().then(setRollouts).catch(console.error);
  }, []);

  const paused = rollouts.filter((r) => r.state === "PAUSED");

  const filtered = rollouts
    .filter((r) => stateFilter === "ALL" || r.state === stateFilter)
    .filter(
      (r) =>
        r.baselineVersion.toLowerCase().includes(filter.toLowerCase()) ||
        r.targetSegmentName.toLowerCase().includes(filter.toLowerCase())
    );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Rollouts</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Step Functions phased rollout: Canary 5% → 25% → 100%.
          Auto-pauses when rollback rate exceeds threshold.
        </p>
      </div>

      {paused.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{paused.length} rollout{paused.length > 1 ? "s" : ""} paused</strong> — rollback rate
            exceeded the auto-pause threshold. Review and resume or roll back.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        {["ALL", "CANARY", "PHASE_25", "PHASE_100", "PAUSED", "COMPLETE", "ROLLED_BACK"].map((s) => (
          <button
            key={s}
            onClick={() => setStateFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              stateFilter === s
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
            }`}
          >
            {STATE_LABEL[s] || s}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search version, segment…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firmware</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Success</TableHead>
                <TableHead>Rollbacks</TableHead>
                <TableHead>Rollback Rate</TableHead>
                <TableHead>Segment</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-400 py-10">No rollouts match.</TableCell>
                </TableRow>
              )}
              {filtered.map((r) => {
                const deployed = r.successCount + r.rollbackCount;
                const pct = r.targetCount > 0 ? Math.round((deployed / r.targetCount) * 100) : 0;
                const rateHigh = r.rollbackRate > r.rollbackRateThreshold;
                return (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/rollouts/${r.id}`)}>
                    <TableCell>
                      <p className="font-mono font-semibold text-sm">v{r.baselineVersion}</p>
                      <p className="text-xs text-slate-400">{r.id}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATE_VARIANT[r.state] || "muted"}>
                        {STATE_LABEL[r.state] || r.state}
                      </Badge>
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <Progress value={pct} className="h-2" />
                      <p className="text-xs text-slate-400 mt-1">{pct}% ({deployed.toLocaleString()} / {r.targetCount.toLocaleString()})</p>
                    </TableCell>
                    <TableCell className="text-sm text-emerald-700 font-medium">{r.successCount.toLocaleString()}</TableCell>
                    <TableCell className="text-sm text-red-600 font-medium">{r.rollbackCount}</TableCell>
                    <TableCell>
                      <span className={`text-sm font-medium ${rateHigh ? "text-red-600" : "text-slate-600"}`}>
                        {rollbackRatePercent(r.rollbackRate)}
                        {rateHigh && <AlertTriangle className="inline ml-1 h-3.5 w-3.5 text-red-500" />}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{r.targetSegmentName}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(r.startedAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/rollouts/${r.id}`); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
